import React, { useState, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ModuleRegistry,
  NumberEditorModule,
  NumberFilterModule,
  PaginationModule,
  RowSelectionModule,
  TextEditorModule,
  TextFilterModule,
  ValidationModule,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useDispatch, useSelector } from "react-redux";
import { GetCashoutlist } from "../../redux/slices/cashoutreq";
import { useNavigate } from "react-router";
import moment from "moment";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";

ModuleRegistry.registerModules([
  NumberEditorModule,
  TextEditorModule,
  TextFilterModule,
  NumberFilterModule,
  RowSelectionModule,
  PaginationModule,
  ClientSideRowModelModule,
  ValidationModule,
]);

function ListofCashouts() {
  const transactions = useSelector((state) => state?.cash?.data);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(GetCashoutlist(navigate));
  }, []);

  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const [selectedImages, setSelectedImages] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [remarks, setRemarks] = useState([]);

  const handleImageClick = (images, index) => {
    setSelectedImages(images);
    setCurrentImageIndex(index);
  };

  const handleStatusClick = (params) => {
    setRemarks(params);
    setOpenModal(true);
  };

  const [columnDefs] = useState([
    {
      field: "cad",
      minWidth: 150,
      headerName: "Transaction",
      cellRenderer: (params) => (params?.value ? params?.value : `********`),
    },
    {
      field: "attachments",
      minWidth: 100,
      headerName: "Attachments",
      cellRenderer: (params) => {
        return params?.value?.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            <button
              className="ml-2 text-blue-500 underline"
              onClick={() => handleImageClick(params?.value, 0)}
            >
              View
            </button>
          </div>
        ) : (
          <span className="text-gray-400">No Attachments</span>
        );
      },
    },
    {
      field: "balance",
      minWidth: 120,
      headerName: "Before Balance",
      cellRenderer: (params) => `$${params?.value}`,
    },
    {
      field: "amount",
      minWidth: 100,
      headerName: "Withdrawal Amount",
      cellRenderer: (params) => `$${params?.value}`,
    },
    {
      field: "balance",
      minWidth: 150,
      maxWidth: 150,
      headerName: " After Balance",
      cellRenderer: (params) =>
        `$${params?.data?.amount > 0 && params?.value - params?.data?.amount}`,
    },
    {
      field: "createdAt",
      minWidth: 150,
      headerName: "Date",
      cellRenderer: (params) =>
        moment(params.value || params.data.value).format("DD/MM/YY hh:mm A"),
    },
    {
      field: "status",
      minWidth: 120,
      headerName: "Status",
      cellRenderer: (params) => (
        <div className="flex justify-start items-center capitalize">
          <span
            className={`mr-1 ${
              params.value === "pending"
                ? "bg-yellow-500"
                : params.value === "rejected"
                ? "bg-red-500"
                : params.value === "paid"
                ? "bg-green-500"
                : ""
            }`}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "15px",
              height: "15px",
              borderRadius: "50%",
            }}
          >
            {params.value === "pending" ? (
              <CheckOutlinedIcon
                fontSize="small"
                style={{ color: "white", fontSize: "12px" }}
              />
            ) : params.value === "rejected" ? (
              <ClearOutlinedIcon
                fontSize="small"
                style={{ color: "white", fontSize: "12px" }}
              />
            ) : params.value === "paid" ? (
              <CheckOutlinedIcon
                fontSize="small"
                style={{ color: "white", fontSize: "12px" }}
              />
            ) : null}
          </span>

          <span
            className={`cursor-pointer ${
              params?.data?.remarks?.length > 0 &&
              params?.data?.remarks[0] != "" &&
              params.value === "rejected"
                ? "text-sky-500 underline"
                : ""
            }`}
            onClick={() => {
              if (
                params?.data?.remarks?.length > 0 &&
                params?.data?.remarks[0] != ""
              ) {
                if (params.value === "rejected") {
                  handleStatusClick(params?.data);
                }
              }
            }}
          >
            {params.value}
          </span>
        </div>
      ),
    },
  ]);

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      filter: true,
      resizable: false,
      sortable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  );

  return (
    <div className="grid-table">
      <div className="ag-theme-alpine custom-table">
        <AgGridReact
          rowData={transactions}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={40}
          domLayout="autoHeight"
          suppressMenuHide={true}
        />
      </div>
      {currentImageIndex >= 0 && (
        <Lightbox
          mainSrc={selectedImages[currentImageIndex]?.image}
          nextSrc={
            selectedImages[(currentImageIndex + 1) % selectedImages.length]
              ?.image
          }
          prevSrc={
            selectedImages[
              (currentImageIndex + selectedImages.length - 1) %
                selectedImages.length
            ]?.image
          }
          onCloseRequest={() => setCurrentImageIndex(-1)}
          onMovePrevRequest={() =>
            setCurrentImageIndex(
              (currentImageIndex + selectedImages.length - 1) %
                selectedImages.length
            )
          }
          onMoveNextRequest={() =>
            setCurrentImageIndex(
              (currentImageIndex + 1) % selectedImages.length
            )
          }
        />
      )}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box className="relative p-4 bg-white shadow-lg rounded-lg w-96 mx-auto mt-20 overflow-auto max-h-60">
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-black"
            onClick={() => setOpenModal(false)}
          >
            <CloseIcon />
          </button>
          <h3 className="text-xl text-gray-600 font-semibold">Remarks :</h3>
          <p className="text-gray-600 break-words">{remarks?.remarks}</p>
        </Box>
      </Modal>
    </div>
  );
}

export default ListofCashouts;
